import { Loader } from '@components/loader/Loader'
import { ApplicationForm } from '@features/applications/components/ApplicationForm'
import { usePatchInvitation } from '@services/applications/mutations/usePatchInvitation'
import { useInvitation } from '@services/applications/queries/useInvitation'
import { ArrowLeft, Check, ChevronRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useIntlLocale } from '@/i18n/languages'

type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED'

const STATUS_STYLES: Record<InvitationStatus, string> = {
  ACCEPTED: 'bg-primary/15 text-primary',
  PENDING: 'bg-peach/20 text-[#b5651d]',
  REJECTED: 'bg-destructive/10 text-destructive'
}

function initials(email?: string) {
  if (!email) return '?'
  return email.slice(0, 2).toUpperCase()
}

export function ApplicationInvitation() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { mutate: patchInvitation } = usePatchInvitation()
  const { data, isLoading, isError } = useInvitation({ id })

  const { t } = useTranslation('application')
  const locale = useIntlLocale()

  if (!id) {
    navigate(-1)
    return null
  }
  if (isLoading) return <Loader />
  if (isError) {
    navigate(-1)
    return null
  }
  if (!data) return <div>No data found</div>

  const status = (data.status ?? 'PENDING') as InvitationStatus
  const invitedOn = data.created_at
    ? new Date(data.created_at).toLocaleDateString(locale)
    : null

  const acceptInvitation = () =>
    patchInvitation({ id, body: { status: 'ACCEPTED' } })

  const declineInvitation = () => {
    patchInvitation({ id, body: { status: 'REJECTED' } })
    navigate('/user/application')
  }

  const retreatInvitation = () =>
    patchInvitation({
      id,
      body: { status: 'PENDING' },
      params: { onSuccess: () => navigate('/user/application') }
    })

  return (
    <div className="mx-auto w-full max-w-[1100px]">
      <div className="my-5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/user/application')}
          aria-label={t('form.common.backToApplications')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/15 bg-white p-0 text-ink transition-colors hover:border-ink/30 hover:bg-ink/[0.03]"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <nav className="flex min-w-0 items-center gap-1.5 text-sm">
          <Link
            to="/user/application"
            className="shrink-0 text-muted-foreground transition-colors hover:text-primary"
          >
            {t('form.labels.applications')}
          </Link>
          <ChevronRight className="h-4 w-4 shrink-0 text-ink/30" />
          <span className="truncate font-semibold text-ink">
            {t('form.sections.roommates.invitationTitle')}
          </span>
        </nav>
      </div>

      <Card className="space-y-5 border-0 p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
              {initials(data.invitor_email)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-semibold text-ink">
                  {data.invitor_email}
                </span>
                <span
                  className={cn(
                    'inline-flex shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    STATUS_STYLES[status]
                  )}
                >
                  {t(`form.common.status.${status}`)}
                </span>
              </div>
              <p className="m-0 mt-0.5 truncate text-sm text-muted-foreground">
                {t('form.sections.roommates.invitedYou')}
                {invitedOn &&
                  ` · ${t('form.sections.roommates.invitedOn', { date: invitedOn })}`}
              </p>
            </div>
          </div>

          {status === 'PENDING' && (
            <div className="flex shrink-0 gap-3">
              <Button onClick={acceptInvitation}>
                <Check className="h-4 w-4" />
                {t('form.common.yes')}
              </Button>
              <Button
                variant="ghostDark"
                className="text-destructive hover:bg-destructive hover:text-white"
                onClick={declineInvitation}
              >
                <X className="h-4 w-4" />
                {t('form.common.no')}
              </Button>
            </div>
          )}
          {status === 'ACCEPTED' && (
            <Button
              variant="ghostDark"
              className="shrink-0 text-destructive hover:bg-destructive hover:text-white"
              onClick={retreatInvitation}
            >
              <X className="h-4 w-4" />
              {t('form.common.retreatInvitation')}
            </Button>
          )}
        </div>

        <ApplicationForm
          data={data.invitor_application}
          handleSubmit={() => undefined}
          isDisabled
        />
      </Card>
    </div>
  )
}
